import {
  ActionIcon,
  Button,
  CopyButton,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import dayjs from "dayjs";

import { buildOnboardingInvitationLink } from "@utils/onboardingInvitation";

import { OnboardingInvitationCredentials } from "@customTypes/accountManagement";


interface Props {
  invitation: OnboardingInvitationCredentials;
  copyEnabled?: boolean;
  disabledMessage?: string;
  showSuccessHeader?: boolean;
  onDone?: () => void;
}

const CopyValue = ({
  value,
  label,
  disabled,
}: {
  value: string;
  label: string;
  disabled: boolean;
}) => (
  <Group
    gap="xs"
    wrap="nowrap"
    style={{
      width: "100%",
      background: "#f8f9fa",
      padding: "12px",
      borderRadius: "8px",
      border: "1px solid #e9ecef",
    }}>
    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text
        size="sm"
        fw={label === "Onboarding code" ? 600 : 400}
        style={{ wordBreak: "break-all" }}>
        {value}
      </Text>
    </Stack>
    <CopyButton value={value} timeout={2000}>
      {({ copied, copy }) => (
        <Tooltip
          label={disabled ? "This invitation cannot be copied" : copied ? "Copied" : "Copy"}
          withArrow
          position="right">
          <ActionIcon
            color={copied ? "teal" : "gray"}
            variant="subtle"
            disabled={disabled}
            aria-label={`Copy ${label.toLowerCase()}`}
            onClick={copy}>
            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  </Group>
);

export const OnboardingInvitationAccess = ({
  invitation,
  copyEnabled = true,
  disabledMessage,
  showSuccessHeader = false,
  onDone,
}: Props) => {
  const link = buildOnboardingInvitationLink(invitation.token);

  return (
    <Stack gap="md" align={showSuccessHeader ? "center" : "stretch"}>
      {showSuccessHeader && (
        <>
          <Text fw={600} ta="center" c="green">
            Onboarding invitation ready
          </Text>
          <Text size="sm" ta="center" c="dimmed">
            Copy the link or code and send it to the prospect.
          </Text>
        </>
      )}

      <CopyValue
        value={link}
        label="Onboarding link"
        disabled={!copyEnabled}
      />
      {invitation.onboarding_code && (
        <CopyValue
          value={invitation.onboarding_code}
          label="Onboarding code"
          disabled={!copyEnabled}
        />
      )}

      <Text size="xs" c="dimmed" ta={showSuccessHeader ? "center" : "left"}>
        Expires {dayjs(invitation.expires_at).format("D MMM YYYY, h:mm A")}
      </Text>
      {!copyEnabled && disabledMessage && (
        <Text size="sm" c="orange.8">
          {disabledMessage}
        </Text>
      )}

      {onDone && (
        <Button variant="light" fullWidth mt="sm" onClick={onDone}>
          Done
        </Button>
      )}
    </Stack>
  );
};
